import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import './index.css';
import { TooltipProvider, Toaster } from 'shared-ui';
import { CircleCheckIcon, InfoIcon, Loader2Icon, TriangleAlertIcon } from 'lucide-react';
import { PersistentThemeProvider } from '@/components/app/PersistentThemeProvider.tsx';
import { AppConfigProvider } from '@/config/AppConfigContext.tsx';
import { loadAppConfig } from '@/config/loadAppConfig.ts';
import { router } from '@/router';
import { AuthProvider } from '@/auth/AuthProvider';
import { AuthErrorView } from '@/auth/AuthErrorView';
import { RootErrorBoundary } from '@/components/app/RootErrorBoundary';
import { createMsalInstance } from '@/auth/msalInstance';
import { getAuthErrorMessage } from '@/auth/authErrors';
import type { PublicClientApplication } from '@azure/msal-browser';

/*
 * Orbit-specific icon overrides for Sonner toasts.
 * The shared-ui <Toaster> spreads {...props} last, so passing `icons` here
 * replaces its internal set entirely — all five types must be declared to
 * avoid falling back to Sonner's plain defaults for non-error toasts.
 * Error uses TriangleAlertIcon; all others mirror shared-ui's choices.
 */
const TOAST_ICONS = {
    success: <CircleCheckIcon className="size-4" />,
    info: <InfoIcon className="size-4" />,
    warning: <TriangleAlertIcon className="size-4" />,
    // Error icon: size/colour are applied via CSS in index.css
    error: <TriangleAlertIcon />,
    loading: <Loader2Icon className="size-4 animate-spin" />,
} as const;

// Surface otherwise-silent runtime failures (the kind that only appear in a
// deployed build) to the console with context. Combined with source maps, this
// makes staging-only errors such as "DE is not defined" diagnosable instead of opaque.
window.addEventListener('error', (event) => {
    const err = event.error instanceof Error ? event.error : undefined;
    console.error('[GlobalError]', event.message, '\n', err?.stack ?? '', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
    });
});
window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    const err = reason instanceof Error ? reason : undefined;
    console.error('[UnhandledRejection]', err?.message ?? reason, '\n', err?.stack ?? '');
});

const appConfig = loadAppConfig(document.querySelector<HTMLScriptElement>('#app-config'));

const root = document.getElementById('root');

if (root === null) {
    throw new Error('No root element found');
}

// TypeScript narrows root to HTMLElement above, but async closures do not carry
// that narrowing — capture it in an explicitly-typed const.
const appRoot: HTMLElement = root;

async function bootstrap() {
    let msalInstance: PublicClientApplication | null = null;
    let initError: Error | null = null;

    try {
        msalInstance = await createMsalInstance(appConfig);
    } catch (e) {
        initError = e instanceof Error ? e : new Error('MSAL initialisation failed');
        // Log the technical error for developers — never log token values
        console.error('[Auth] Failed to initialise MSAL:', e);
    }

    createRoot(appRoot).render(
        <StrictMode>
            <AppConfigProvider config={appConfig}>
                <PersistentThemeProvider>
                    <Toaster position="bottom-center" icons={TOAST_ICONS} />
                    {initError !== null || msalInstance === null ? (
                        // Fatal auth config error — show before the full app tree
                        <AuthErrorView
                            message={getAuthErrorMessage(initError ?? new Error('MSAL not initialised'))}
                            onRetry={() => {
                                window.location.reload();
                            }}
                        />
                    ) : (
                        <RootErrorBoundary>
                            <AuthProvider msalInstance={msalInstance}>
                                <TooltipProvider>
                                    <RouterProvider router={router} />
                                </TooltipProvider>
                            </AuthProvider>
                        </RootErrorBoundary>
                    )}
                </PersistentThemeProvider>
            </AppConfigProvider>
        </StrictMode>,
    );
}

void bootstrap();
